import { isNullOrEmpty } from 'common-utils';
import React from 'react';
import { ComponentType } from 'dto/raptor-component.dto';
import { ManifestPlatformDto } from 'dto/raptor-component-version.dto';
import { DockerImageDto } from 'dto/raptor-docker-image.dto';
import { UploadFileDto } from 'dto/upload-file.dto';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { usePagination } from 'hooks/use-table-pagination.hook';
import { useRaptorComponentsStore, useUploadFilesStore } from 'store';
import {
    BasicFormWithAlerts,
    Form,
    HorizontalStepper,
    IStepItem,
    notification,
    Skeleton,
    Spinner,
} from 'ui-lib';
import css from '../raptor-components.css';
import { RaptorComponentVersionHelpers, validateComponentVersionFormat } from './component-version.helpers';
import { IComponentVersionFormValues, IComponentVersionWizardProps, IConfigureStepProps } from './component-version-wizard.types';
import { CatapultConfigureStep } from './steps/catapult-configure-step';
import { DockerComposeConfigureStep } from './steps/docker-compose-configure-step';
import { DockerFileConfigureStep } from './steps/docker-file-configure-step';
import { DockerImageConfigureStep } from './steps/docker-image-configure-step';
import { GeneralStep } from './steps/general-step';
import { ReviewStep } from './steps/review-step';

const DEFAULT_PLATFORM_ARCHITECTURE = 'aarch64';
const GENERAL_STEP_CODE = 'general';
const MANIFEST_PLATFORMS_STEP_CODE = 'manifest-platforms';
const CONFIGURE_MANIFESTS_STEP_CODE = 'configure-manifests';
const REVIEW_STEP_CODE = 'review';

export const ComponentVersionWizard =(props: IComponentVersionWizardProps): JSX.Element => {
    const [form] = Form.useForm<IComponentVersionFormValues>();

    const raptorComponentsStore = useRaptorComponentsStore();
    const uploadFilesStore = useUploadFilesStore();
    const wizardDataLoadingHook = useLoadingStates();

    const [selectedPlatformUuids, setSelectedPlatformUuids] = React.useState<string[]>([]);
    const [manifestPlatforms, setManifestPlatforms] = React.useState<ManifestPlatformDto[]>([]);
    const selectedPlatforms = React.useMemo(() => {
        return manifestPlatforms.filter((platform) => selectedPlatformUuids.includes(platform.uuid));
    }, [manifestPlatforms, selectedPlatformUuids]);

    const artifactsSource = usePagination<UploadFileDto>({
        mode: 'offset',
        pageSize: 20,
        fetchData: async ({ page = 1, pageSize }) => {
            const type = RaptorComponentVersionHelpers.getArtifactUploadFileType(props.component.type);
            if (!type) {
                return { data: [] };
            }
            const data = await uploadFilesStore.getArtifacts(props.account.id, {
                type,
                offset: (page - 1) * pageSize,
                limit: pageSize,
                includeSharedFiles: true,
            });
            return { data };
        },
    });
    const dockerImagesSource = usePagination<DockerImageDto>({
        mode: 'offset',
        pageSize: 20,
        fetchData: async ({ page = 1, pageSize }) => {
            if (props.component.type === ComponentType.CATAPULT) {
                return { data: [] };
            }
            const data = await raptorComponentsStore.listDockerImages(props.account.id, props.account.uuid, {
                offset: (page - 1) * pageSize,
                limit: pageSize,
            });
            return { data };
        },
    });

    const [canGoNextByStepCode, setCanGoNextByStepCode] = React.useState<Record<string, boolean>>({
        [GENERAL_STEP_CODE]: false,
        [MANIFEST_PLATFORMS_STEP_CODE]: false,
        [CONFIGURE_MANIFESTS_STEP_CODE]: false,
    });

    const getStepValidity = (): Record<string, boolean> => {
        const version = form.getFieldValue('version');
        const platformUuids = form.getFieldValue('platformUuids') || [];
        const trimmedVersion = version?.trim();
        const isVersionValid = props.versionDisabled
            ? !isNullOrEmpty(trimmedVersion)
            : !isNullOrEmpty(trimmedVersion) && !validateComponentVersionFormat(trimmedVersion);

        return {
            [GENERAL_STEP_CODE]: isVersionValid,
            [MANIFEST_PLATFORMS_STEP_CODE]: platformUuids.length > 0,
            [CONFIGURE_MANIFESTS_STEP_CODE]: RaptorComponentVersionHelpers.isConfigurationValid(
                props.component.type,
                form.getFieldsValue(true),
            ),
        };
    };

    const refreshStepValidity = () => {
        setCanGoNextByStepCode(getStepValidity());
    };

    React.useEffect(() => {
        form.setFieldsValue(props.initialValues);
        setSelectedPlatformUuids(props.initialValues.platformUuids || []);
        refreshStepValidity();
    }, [props.initialValues]);

    React.useEffect(() => {
        let isMounted = true;

        const loadWizardData = async (): Promise<void> => {
            const result = await wizardDataLoadingHook.withLoading(async () => {
                return raptorComponentsStore.listManifestPlatforms(props.account.id);
            });

            if (!isMounted) {
                return;
            }

            if (result.isFail()) {
                notification.error({
                    message: 'Failed to load component version wizard data',
                    description: result.errorMessage,
                });
                return;
            }

            setManifestPlatforms(result.getOrThrow());
        };

        loadWizardData();

        return () => {
            isMounted = false;
        };
    }, [props.account.id, props.component.type]);

    // Make aarch64 platform as default
    React.useEffect(() => {
        if (manifestPlatforms.length === 0 || !isNullOrEmpty(form.getFieldValue('platformUuids'))) {
            return;
        }

        const defaultPlatform = manifestPlatforms.find(
            (platform) => platform.architecture.toLowerCase() === DEFAULT_PLATFORM_ARCHITECTURE,
        );
        if (!defaultPlatform) {
            notification.error({
                message: 'Manifest not found',
                description: `No manifest platform with architecture "${DEFAULT_PLATFORM_ARCHITECTURE}" is available.`,
            });
            return;
        }

        form.setFieldsValue({ platformUuids: [defaultPlatform.uuid] });
        setSelectedPlatformUuids([defaultPlatform.uuid]);
        refreshStepValidity();
    }, [manifestPlatforms]);

    const getConfigurationErrorMessage = (): string | null => {
        const errorMessage = RaptorComponentVersionHelpers.validateConfigurationJson(props.component.type, form.getFieldValue('configurationJson'));

        if (!errorMessage) {
            return null;
        }

        notification.error({
            message: 'Invalid configuration JSON',
            description: errorMessage,
        });
        return errorMessage;
    };

    const onSave = async () => {
        if (Object.values(getStepValidity()).some((isValid) => !isValid)) {
            return;
        }

        const errorMessage = getConfigurationErrorMessage();
        if (errorMessage) {
            return;
        }

        await props.onSubmit(form.getFieldsValue(true));
    };

    const renderConfigureStep = () => {
        const configureStepProps: IConfigureStepProps = {
            selectedPlatforms,
            accountUuid: props.account.uuid,
            artifactsSource,
            dockerImagesSource,
        };

        if (props.component.type === ComponentType.CATAPULT) {
            return <CatapultConfigureStep {...configureStepProps} />;
        }

        if (props.component.type === ComponentType.DOCKER_FILE) {
            return <DockerFileConfigureStep {...configureStepProps} />;
        }

        if (props.component.type === ComponentType.DOCKER_COMPOSE) {
            return <DockerComposeConfigureStep {...configureStepProps} />;
        }

        return <DockerImageConfigureStep {...configureStepProps} />;
    };

    const stepItems: IStepItem[] = [
        {
            code: GENERAL_STEP_CODE,
            label: 'General',
            description: 'Confirm the component context and enter the version details.',
            content: (
                <GeneralStep
                    component={props.component}
                    versionDisabled={props.versionDisabled}
                    versionValidator={validateComponentVersionFormat}
                />
            ),
            navigation: {
                next: {
                    disabled: !canGoNextByStepCode[GENERAL_STEP_CODE],
                },
            },
        },
        {
            code: CONFIGURE_MANIFESTS_STEP_CODE,
            label: 'Configure manifests',
            description: 'Provide the configuration values required for the selected component type and platforms.',
            content: renderConfigureStep(),
            navigation: {
                next: {
                    disabled: !canGoNextByStepCode[CONFIGURE_MANIFESTS_STEP_CODE],
                },
            },
        },
        {
            code: REVIEW_STEP_CODE,
            label: 'Review',
            description: 'Review the component version before saving it.',
            content: (
                <ReviewStep
                    component={props.component}
                    selectedPlatforms={selectedPlatforms}
                    form={form}
                />
            ),
        },
    ];

    return (
        <Skeleton loading={props.loading || wizardDataLoadingHook.loading}>
            <Spinner spinning={props.saving} className={css.spinner}>
                <BasicFormWithAlerts
                    form={form}
                    className={css['wizard-form']}
                    errorMessages={props.errorMessages}
                    scrollToFirstError={true}
                    onFieldsChange={() => {
                        setSelectedPlatformUuids(form.getFieldValue('platformUuids') || []);
                        refreshStepValidity();
                    }}
                    items={[
                        <React.Fragment>
                            <HorizontalStepper
                                className={css['wizard-stepper']}
                                items={stepItems}
                                currentStepCode={GENERAL_STEP_CODE}
                                actions={{
                                    canGoBack: true,
                                    cancel: {
                                        onClick: props.onCancel,
                                    },
                                    finish: {
                                        label: 'Save',
                                        disabled: Object.values(canGoNextByStepCode).some((isValid) => !isValid),
                                        onClick: onSave,
                                    },
                                }}
                            />
                        </React.Fragment>,
                    ]}
                />
            </Spinner>
        </Skeleton>
    );
};
